import { computed, DestroyRef, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ENVIRONMENT } from '@privanot/shared-ui/providers';
import { accessTokenVersion } from '@client/auth/access-token-version';
import { AuthErrorKind, authErrorKind, ENTRA_USER_ROLE } from '@client/auth/auth-error';
import { AuthSession } from '@client/auth/interfaces/auth-session.interface';
import { EntraClientConfig } from '@client/auth/interfaces/entra-client-config.interface';
import { ElectronEntraAuthProvider } from '@client/auth/providers/electron-entra-auth-provider';
import { EntraAuthProvider } from '@client/auth/providers/entra-auth-provider';
import { ActiveRun } from '@client/shared/services/active-run';
import { SessionNavigator } from './session-navigator';

export type AuthStatus = 'initializing' | 'disabled' | 'signedOut' | 'signingIn' | 'signedIn';

export const AUTH_CONFIG_PATH = 'client/auth/config';
export const AUTH_SIGN_OUT_PATH = 'client/auth/sign-out';
export const AUTH_TOKEN_PATH = 'client/auth/token';

/** Renew a cached token once it has less than this left. */
const RENEW_MARGIN_MS = 2 * 60 * 1000;

/**
 * Renew this long before the token expires, without waiting for a request: the Client Service needs a valid token for
 * runs that go on in the background while nobody uses the app.
 */
export const RENEW_AHEAD_MS = 5 * 60 * 1000;

/** Retry a renewal that failed for another reason than an ended session (e.g. Microsoft unreachable) this often. */
export const RENEW_RETRY_MS = 30 * 1000;

const escapeHtml = (text: string): string =>
    text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const browserPage = (text: string): string =>
    `<!doctype html><html><head><meta charset="utf-8"><title>Privanot</title></head>` +
    `<body style="font-family: sans-serif; padding: 2rem;"><p>${escapeHtml(text)}</p></body></html>`;

export type EntraAuthProviderFactory = (config: EntraClientConfig) => EntraAuthProvider | Promise<EntraAuthProvider>;

export const ENTRA_AUTH_PROVIDER_FACTORY = new InjectionToken<EntraAuthProviderFactory>('ENTRA_AUTH_PROVIDER_FACTORY', {
    providedIn: 'root',
    factory: () => {
        const translate = inject(TranslateService);
        return async (config: EntraClientConfig) => {
            if (window.privanotAuth) {
                return new ElectronEntraAuthProvider(config, window.privanotAuth, () => ({
                    successHtml: browserPage(translate.instant('auth.browserPage.success')),
                    errorHtml: browserPage(translate.instant('auth.browserPage.error')),
                }));
            }
            // MSAL Browser is loaded only by hosted clients that have Entra enabled.
            const { BrowserEntraAuthProvider } = await import('@client/auth/providers/browser-entra-auth-provider');
            return new BrowserEntraAuthProvider(config);
        };
    },
});

/**
 * Entra ID sign-in state for the Client App. When the client has Entra disabled it settles on 'disabled' and the app
 * runs as before; otherwise nothing past the sign-in screen is reachable until 'signedIn'.
 */
@Injectable({ providedIn: 'root' })
export class EntraAuthService {
    private readonly http = inject(HttpClient);
    private readonly env = inject(ENVIRONMENT);
    private readonly createProvider = inject(ENTRA_AUTH_PROVIDER_FACTORY);
    private readonly navigator = inject(SessionNavigator);
    private readonly activeRun = inject(ActiveRun);

    private provider: EntraAuthProvider | null = null;
    private session: AuthSession | null = null;
    private initializing: Promise<void> | null = null;
    private renewing: Promise<AuthSession> | null = null;
    private leaving: Promise<void> | null = null;
    private signingOut = false;
    private renewTimer: ReturnType<typeof setTimeout> | null = null;

    private readonly _status = signal<AuthStatus>('initializing');
    private readonly _error = signal<AuthErrorKind | null>(null);
    private readonly _enabled = signal<boolean>(false);
    private readonly _displayName = signal<string | null>(null);

    public readonly status = this._status.asReadonly();
    public readonly error = this._error.asReadonly();
    public readonly enabled = this._enabled.asReadonly();
    public readonly displayName = this._displayName.asReadonly();
    public readonly isReady = computed(() => this._status() === 'disabled' || this._status() === 'signedIn');

    constructor() {
        inject(DestroyRef).onDestroy(() => this.clearRenewTimer());
        // Hosted: pressing Back on Microsoft's page can restore this page from the back/forward cache mid-redirect.
        window.addEventListener('pageshow', (event) => {
            if ((event as PageTransitionEvent).persisted && this._status() === 'signingIn') {
                this._status.set('signedOut');
            }
        });
    }

    public initialize(): Promise<void> {
        return (this.initializing ??= this.load());
    }

    public async retry(): Promise<void> {
        this.initializing = null;
        this._error.set(null);
        await this.initialize();
    }

    public async signIn(): Promise<void> {
        if (!this.provider) {
            return;
        }
        this._error.set(null);
        this._status.set('signingIn');
        try {
            const session = await this.provider.signIn();
            if (session) {
                await this.apply(session);
            }
        } catch (err) {
            const kind = authErrorKind(err);
            this._error.set(kind === 'cancelled' ? null : kind);
            this._status.set('signedOut');
        }
    }

    public async cancelSignIn(): Promise<void> {
        await this.provider?.cancel();
    }

    /** Bearer token for the Client Service, or '' when Entra is disabled or nobody is signed in. */
    public async getAccessToken(): Promise<string> {
        if (this._status() !== 'signedIn' || !this.provider || !this.session) {
            return '';
        }
        if (this.session.expiresOn - Date.now() > RENEW_MARGIN_MS) {
            return this.session.accessToken;
        }

        try {
            return (await this.renew(false)).accessToken;
        } catch (err) {
            if (authErrorKind(err) === 'sessionExpired') {
                void this.leaveSession();
            }
            throw err;
        }
    }

    /**
     * Gets a newly issued token now and hands it to the Client Service: ahead of expiry, and whenever the service asks
     * (its `renewToken` message). A session that can't be renewed any more is left; other failures are retried while
     * the current token is still valid.
     */
    public async renewNow(): Promise<void> {
        if (this._status() !== 'signedIn' || !this.provider || !this.session) {
            return;
        }
        try {
            await this.renew(true);
        } catch (err) {
            if (authErrorKind(err) === 'sessionExpired') {
                void this.leaveSession();
            } else if (this.session && this.session.expiresOn > Date.now()) {
                this.scheduleRenewal(RENEW_RETRY_MS);
            }
        }
    }

    /** The Client Service refused the token or ended the session (sign-out elsewhere): back to sign-in. */
    public async handleUnauthorized(): Promise<void> {
        if (this.signingOut) {
            // This window's own sign-out: it shows "signed out", not "session expired".
            return;
        }
        await this.leaveSession();
    }

    public async signOut(): Promise<void> {
        this.signingOut = true;
        try {
            // The auth interceptor adds the bearer token.
            await firstValueFrom(this.http.post(`${this.env.BASE_URL}/${AUTH_SIGN_OUT_PATH}`, null));
        } catch {
            // Best effort: the local session still ends.
        }
        try {
            await this.provider?.signOut();
        } catch {
            // Nothing else to clean up; the reload below drops the in-memory state.
        }
        this.clearRenewTimer();
        this.session = null;
        this.navigator.toSignIn('signedOut');
    }

    /**
     * Ends the local session before reloading: otherwise the reload restores the very token the service refused
     * (MSAL cache / Electron main process) and the app loops between sign-in and work packages.
     */
    private leaveSession(): Promise<void> {
        return (this.leaving ??= (async () => {
            this.clearRenewTimer();
            this.session = null;
            try {
                await this.provider?.signOut();
            } catch {
                // The reload still drops the in-memory state.
            }
            // The Client Service finishes the file in progress and fails the rest; the sign-in screen says so.
            this.navigator.toSignIn(this.activeRun.isRunning() ? 'expiredDuringRun' : 'expired');
        })());
    }

    private async load(): Promise<void> {
        let config: EntraClientConfig;
        try {
            config = await firstValueFrom(this.http.get<EntraClientConfig>(`${this.env.BASE_URL}/${AUTH_CONFIG_PATH}`));
        } catch {
            this._status.set('signedOut');
            this._error.set('serviceUnavailable');
            return;
        }

        if (!config.enabled) {
            this._enabled.set(false);
            this._status.set('disabled');
            return;
        }

        this._enabled.set(true);
        try {
            this.provider = await this.createProvider(config);
            const restored = await this.provider.initialize();
            if (restored) {
                await this.apply(restored);
                return;
            }
        } catch (err) {
            this._error.set(authErrorKind(err));
        }
        this._status.set('signedOut');
    }

    private async apply(session: AuthSession): Promise<void> {
        // v1 tokens: the app registration lacks requestedAccessTokenVersion 2, and Privanot refuses them.
        const refusal = !session.roles.includes(ENTRA_USER_ROLE)
            ? 'accessDenied'
            : accessTokenVersion(session.accessToken) === '1.0'
              ? 'tokenVersion'
              : null;
        if (refusal) {
            this.session = null;
            await this.provider?.signOut();
            this._error.set(refusal);
            this._status.set('signedOut');
            return;
        }
        this._displayName.set(session.displayName);
        this._error.set(null);
        this._status.set('signedIn');
        this.setSession(session);
    }

    private async renew(forceRefresh: boolean): Promise<AuthSession> {
        try {
            const session = await (this.renewing ??= this.provider!.getToken({ forceRefresh }));
            this.setSession(session);
            return session;
        } finally {
            this.renewing = null;
        }
    }

    /** Every new token: keep it, hand it to the Client Service and plan its renewal. */
    private setSession(session: AuthSession): void {
        if (this.session === session) {
            return;
        }
        this.session = session;
        void this.pushToken(session.accessToken);
        this.scheduleRenewal(session.expiresOn - RENEW_AHEAD_MS - Date.now());
    }

    /** Best effort: a missed hand-over is repaired when the service asks for a token (`renewToken`). */
    private async pushToken(accessToken: string): Promise<void> {
        try {
            await firstValueFrom(
                this.http.post(`${this.env.BASE_URL}/${AUTH_TOKEN_PATH}`, null, {
                    headers: new HttpHeaders({ Authorization: `Bearer ${accessToken}` }),
                }),
            );
        } catch {
            // Nothing to do here; see above.
        }
    }

    private scheduleRenewal(delayMs: number): void {
        this.clearRenewTimer();
        // Never sooner than the retry interval: a token that is already close to expiry must not renew in a loop.
        this.renewTimer = setTimeout(
            () => {
                this.renewTimer = null;
                void this.renewNow();
            },
            Math.max(delayMs, RENEW_RETRY_MS),
        );
    }

    private clearRenewTimer(): void {
        if (this.renewTimer !== null) {
            clearTimeout(this.renewTimer);
            this.renewTimer = null;
        }
    }
}
